import { Decimal } from 'decimal.js';
import DineroFactory, { Dinero } from 'dinero.js';

export type CurrencyCode = 'USD' | 'VES' | 'BOB';
const decimalPlace: number = 10000000000;
const DEFAULT_CALCULATION_DECIMAL_PLACES = 5;

export class MoneyAdapter {
    static fromFloat(value: number, currency: CurrencyCode = 'USD') {
        const cents = new Decimal(value).mul(decimalPlace).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();

        return DineroFactory({ amount: cents, currency: currency });
    }

    static toFloat(m: Dinero): number {
        return m.toJSON().amount / decimalPlace;
    }

    static fromFloat2(value: number, currency: CurrencyCode = 'USD') {
        const cents = new Decimal(value).mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();

        return DineroFactory({ amount: cents, currency: currency });
    }

    static toFloat2(m: Dinero): number {
        return m.toJSON().amount / 100;
    }

    static toDecimalPlaces(value: number, decimalPlaces: number = DEFAULT_CALCULATION_DECIMAL_PLACES): number {
        if (value === null || value === undefined || Number.isNaN(Number(value))) return 0;
        return new Decimal(value).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP).toNumber();
    }

    static format(m: Dinero, locale = 'es-VE'): string {
        const amount = this.toFloat(m);
        const currencyCode = m.toJSON().currency;

        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currencyCode,
        }).format(amount);
    }

    static calculateTax(base: Dinero, rate: number): Dinero {
        const baseFloat = this.toFloat(base);
        const tax = new Decimal(baseFloat).mul(rate).toNumber();

        return this.fromFloat(tax, base.toJSON().currency as CurrencyCode);
    }

    static convertCurrency(base: Dinero, rate: number, targetCurrency: CurrencyCode): Dinero {
        const floatAmount = this.toFloat(base);
        const converted = new Decimal(floatAmount).mul(rate).toNumber();

        return this.fromFloat(converted, targetCurrency);
    }

    static sum(moneys: Dinero[]): Dinero {
        if (moneys.length === 0) throw new Error('Empty money array');

        const currency = moneys[0].toJSON().currency;

        const total = moneys.reduce((acc, current) => {
            if (current.toJSON().currency !== currency) {
                throw new Error('Currency mismatch in sum');
            }
            return acc.add(current);
        });

        return total;
    }

    /**
     * Formatea un valor monetario para mostrar en inputs
     * Garantiza máximo 2 decimales y elimina imprecisiones de punto flotante
     *
     * @param value Valor numérico a formatear
     * @returns Valor formateado con la mayor cantidad de decimales posibles
     */
    static formatForInput(value: number): number {
        if (value == null || isNaN(value)) return 0;

        // Usar MoneyAdapter para eliminar imprecisiones de punto flotante
        const adapter = this.fromFloat(value);
        const cleanValue = this.toFloat(adapter);

        // Redondear a la mayor cantidad de decimales posibles
        return Math.round(cleanValue * 100) / 100;
    }

    /**
     * Formatea un valor monetario para mostrar en inputs como string
     * Útil para binding directo en templates
     *
     * @param value Valor numérico a formatear
     * @returns String formateado con máximo 2 decimales
     */
    static formatForInputString(value: number): string {
        const formatted = this.formatForInput(value);
        return formatted.toFixed(2);
    }
}
